import {
  steamNegativeHoverTips,
  steamPositiveHoverTips,
} from '@op/components/BubbleCommon';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import {
  getSteamSupportLanguage,
  getSteamSupportLanguageTimeline,
} from '@op/services';
import { getPercentage, getRationPercentValue } from '@op/utils';
import { DBO_REQ, DBO_V } from '@op/utils/format';
import { calculateSteamReviewRate } from '@op/utils/text';
import { allTimeFormatObj, formatSimpleTimeFunc } from '@op/utils/time';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';

export function useSteamPercentage({
  keys,
  data,
  filter,
  isSelected = false,
}: {
  keys: Array<string>;
  data: any;
  filter: any;
  isSelected?: boolean;
}) {
  function getKeysValue(listInfo: any) {
    return keys.map((key: string) => {
      return listInfo?.[key] || {};
    });
  }

  const [steamRenderBar, steamRenderReviewTotal, time] = useMemo(() => {
    const { list } = data || {};
    const [totalItem, noRecommendItem, recommendItem, allReviewsTotalItem] =
      getKeysValue(list);

    const recommendReview = recommendItem?.review || 0;
    const noRecommendReview = noRecommendItem?.review || 0;
    const totalReview = totalItem?.review || 0;

    const barList = [
      {
        color: '#69D19C',
        text: 'Positive',
        width: getPercentage(recommendReview, totalReview, 0),
        value: recommendReview,
        icon: 'good-icon',
        tipsMap: steamPositiveHoverTips,
      },
      {
        color: '#F17C86',
        text: 'Negative',
        width: getPercentage(noRecommendReview, totalReview, 0),
        value: noRecommendReview,
        icon: 'no-good-icon',
        tipsMap: steamNegativeHoverTips,
      },
    ];

    let time = filter?.end_time;
    const { start_time, end_time, date_type } = filter || {};
    if (isSelected) {
      const isOneDay =
        formatSimpleTimeFunc(start_time) === formatSimpleTimeFunc(time) &&
        date_type !== 'hourly';

      if (isOneDay) {
        time = formatSimpleTimeFunc(start_time);
      } else {
        time =
          formatSimpleTimeFunc(start_time, allTimeFormatObj[date_type]) +
          ' ~ ' +
          formatSimpleTimeFunc(time, allTimeFormatObj[date_type]);
      }
    } else {
      time = formatSimpleTimeFunc(end_time);
    }

    if (!totalItem?.name) {
      const score = recommendReview / totalReview;
      totalItem.name = calculateSteamReviewRate(score);
    }

    return [barList, totalItem, time, allReviewsTotalItem];
  }, [data, filter]);

  const percentInfo = useMemo(() => {
    const { list, old_list, link_of_end_time, link_of_start_time } = data || {};
    const { start_time, end_time } = filter || {};
    const [totalItem] = getKeysValue(list || []);
    const [oldTotalItem] = getKeysValue(old_list || []);
    const totalReview = totalItem?.review || 0;
    const oldTotalReview = oldTotalItem?.review || 0;

    return {
      oldTime: [link_of_start_time, link_of_end_time],
      newTime: [start_time, end_time],
      oldValue: oldTotalReview,
      newValue: totalReview,
      percentage: getRationPercentValue(totalReview, oldTotalReview),
    };
  }, [data, filter]);

  return {
    steamRenderBar,
    steamRenderReviewTotal,
    time,
    percentInfo,
  };
}

export function useGeyEmptyText({ wholeGameInfo, isRecent, filter }: any) {
  const emptyText = useMemo(() => {
    if (!isRecent) return undefined;
    // 减少 30 天
    const dateAReduced = moment(filter?.end_time).subtract(30, 'days');
    // 假设 B 日期为 2023-03-01
    const dateB = moment(wholeGameInfo.release_time, 'YYYY-MM-DD');
    // 比较两个日期
    if (dateAReduced.isBefore(dateB)) {
      return 'This game has been released for less than 30 days and currently does not have Recent Reviews Rating on Steam.';
    }

    return undefined;
  }, [wholeGameInfo, isRecent, filter]);

  return { emptyText };
}

export function useEmptyInfo({
  chartMap,
  timelineKey,
  userScore,
  keys,
  wholeGameInfo,
  isRecent,
  filter,
}: any) {
  const [chartData, chartEmpty, steamData, steamGameEmpty, allEmpty] =
    useMemo(() => {
      const chartData = chartMap[timelineKey] || [];
      const chartEmpty =
        chartData.length === 0 ||
        !chartData.some((item: any) => item.review > 0 || item.score > 0);

      const scoreData = userScore.steam || {};
      const key = keys[0];
      const allData = scoreData?.list?.[key] || {};
      const steamGameEmpty = allData.review === 0;

      return [
        chartData,
        chartEmpty,
        scoreData,
        steamGameEmpty,
        chartEmpty && steamGameEmpty,
      ];
    }, [chartMap, userScore]);

  const { emptyText } = useGeyEmptyText({ wholeGameInfo, isRecent, filter });

  return {
    chartData,
    chartEmpty,
    steamData,
    steamGameEmpty,
    allEmpty,
    emptyText,
  };
}

export function useSteamSupportLanguage({ filter, extraData }: any) {
  const valueKey = 'language_en';
  const [records, setRecords] = useState<any>({});
  const [error, setError] = useState(false);
  const [translateLanguage, setTranslateLanguage] = useState<{
    [key in string]: { 'en-US': string; 'zh-CN': string };
  }>({});
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const { loading, wrapperRequest } = useCancelRequestHook();
  const { sortBy } = extraData;
  const [originalData, setOriginalData] = useState([]);
  const [languageTimeline, setLanguageTimeline] = useState<{
    [key in string]: any;
  }>({});
  const [metric, setMetric] = useState<'all_reviews' | 'positive'>(
    'all_reviews',
  );

  function requestData() {
    setRecords({});
    setSelectedLanguage('');
    setLanguageTimeline({});
    if (!filter.edition_unified_id || !filter.start_time || !filter.end_time)
      return;
    setError(false);
    wrapperRequest((cancelFunc) =>
      getSteamSupportLanguage(
        { ...filter, should_include_timeline: true },
        cancelFunc,
      )
        .then((data: any) => {
          const { list = [] } = data || {};
          const timelineObj: { [key in string]: any } = {};
          const oriData = list.map((item: any) => {
            const {
              timeline,
              timeline_of_start_time,
              timeline_of_end_time,
              ...barInfo
            } = item;

            const chartData = DBO_REQ.moreKeys({
              timeline,
              timeline_of_start_time,
              timeline_of_end_time,
              filter,
              zeroIsNull: true,
              needTable: true,
              moreLineTable: true,
              fixedKeyList: ['all_reviews', 'positive'],
              addCountValue: (item) => {
                const { positive_reviews = 0, all_reviews = 0 } = item || {};
                return {
                  positive: positive_reviews / all_reviews,
                };
              },
            });
            const key = barInfo[valueKey];
            timelineObj[key] = { ...(chartData || {}), time: filter.end_time };

            return barInfo;
          });

          setLanguageTimeline(timelineObj);
          setOriginalData(oriData);
        })
        .catch(() => {
          setError(true);
        }),
    );
  }

  const formatOriginalData = (data: any, sortBy: string) => {
    data = Array.isArray(data) ? data : [];
    const dataY: string[] = [];
    const negative: {
      originName: string;
      value: number;
      total: number;
    }[] = [];
    const positive: {
      originName: string;
      value: number;
      total: number;
    }[] = [];
    const translateMap: any = {};

    const total: number[] = [];
    data?.sort((a: any, b: any) => {
      if (a[valueKey]?.toLocaleLowerCase() === 'others') return -1;
      if (b[valueKey]?.toLocaleLowerCase() === 'others') return 1;
      if (sortBy === 'all_reviews') {
        return (a?.[sortBy] || 0) - (b?.[sortBy] || 0);
      }
      const aValue = a?.[sortBy] || 0;
      const aTotal = a?.all_reviews || 0;
      const bValue = b?.[sortBy] || 0;
      const bTotal = b?.all_reviews || 0;

      if (aTotal === 0 && bTotal === 0) {
        return 0;
      }
      if (aTotal === 0 && bTotal !== 0) {
        return -1;
      }
      if (aTotal !== 0 && bTotal === 0) {
        return 1;
      }

      try {
        const aPercent = DBO_V.percentageValue(aValue, aTotal);
        const bPercent = DBO_V.percentageValue(bValue, bTotal);
        return aPercent - bPercent;
      } catch (error) {
        return 0;
      }
    });

    const table = data.map((val: any) => {
      const {
        all_reviews,
        negative_reviews,
        positive_reviews,
        language_code,
        language_cn,
        language_en,
      } = val || {};

      const key = val[valueKey];

      translateMap[key] = {
        'en-US': language_en,
        'zh-CN': language_cn,
      };

      dataY.push(key);
      negative.push({
        originName: key,
        value: negative_reviews * -1,
        total: all_reviews,
      });
      positive.push({
        originName: key,
        value: positive_reviews,
        total: all_reviews,
      });
      total.push(all_reviews);

      return {
        ...val,
        language: key,
        positive: positive_reviews / all_reviews,
        negative: negative_reviews / all_reviews,
      };
    });
    table.reverse();
    const result = {
      bar: { dataY, negative, positive, total },
      table,
    };
    if (selectedLanguage === '') {
      setSelectedLanguage(dataY?.[dataY.length - 1] || '');
    }
    setTranslateLanguage(translateMap);
    setRecords(result || {});
  };

  useEffect(() => {
    requestData();
  }, [filter]);

  useEffect(() => {
    if (originalData) {
      formatOriginalData(originalData, sortBy);
    }
  }, [originalData, sortBy]);

  const languageData = useMemo(() => {
    if (!selectedLanguage) return {};

    return languageTimeline?.[selectedLanguage] || {};
  }, [languageTimeline, selectedLanguage]);

  const timeline = useMemo(() => {
    const legend: Array<string> = [];
    let xAxis: Array<string> = [];
    let isEmpty = true;

    const series = Object.keys(languageTimeline).map((key, index) => {
      const value = languageTimeline[key];
      if (index === 0) {
        xAxis = value.xAxis;
      }
      legend.push(key);
      if (!value.isEmpty && isEmpty) {
        isEmpty = false;
      }
      return {
        name: key,
        type: 'line',
        data: value.seriesObj?.[metric] || [],
      };
    });

    return { legend, xAxis, series, isEmpty };
  }, [metric, languageTimeline]);

  return {
    data: records,
    loading,
    error,
    refetch: requestData,
    translateLanguage,
    selectedLanguage,
    setSelectedLanguage,
    languageData,
    metric,
    setMetric,
    timeline,
  };
}

export function useSteamSupportLanguageTimeline({ filter, extraData }: any) {
  const [records, setRecords] = useState<any>({});
  const { loading, wrapperRequest, error } = useCancelRequestHook();
  function requestTimeline() {
    if (!filter.edition_unified_id || !filter.steam_language) return;
    setRecords({});
    wrapperRequest((cancelFunc) =>
      getSteamSupportLanguageTimeline(filter, cancelFunc).then((data: any) => {
        const chartData = DBO_REQ.moreKeys({
          ...data,
          filter,
          zeroIsNull: true,
          needTable: true,
          moreLineTable: true,
          fixedKeyList: ['all_reviews', 'positive'],
          addCountValue: (item) => {
            const { positive_reviews = 0, all_reviews = 0 } = item || {};
            return {
              positive: positive_reviews / all_reviews,
            };
          },
        });
        setRecords({ ...(chartData || {}), time: filter.end_time });
      }),
    );
  }

  useEffect(() => {
    requestTimeline();
  }, [filter]);

  return { data: records, loading, error, refetch: requestTimeline };
}
